import { useEffect, useState } from 'react';

import { api } from '../lib/api';
import type { PricedCart } from '../lib/api';
import { money } from '../lib/i18n';
import { useSeo } from '../lib/head';
import { Link, useNavigate } from '../lib/router';
import { privateMeta } from '../lib/seo';
import { useStore } from '../lib/store';

/**
 * Корзина.
 *
 * Содержимое хранится у покупателя, но суммы всегда приходят с сервера.
 * Поэтому при каждом изменении количества идёт запрос: цена могла измениться,
 * а остаток — закончиться. Лучше узнать об этом здесь, чем на оплате.
 */
export function CartPage() {
  const { lang, currency, t, lines, setQty, remove } = useStore();
  useSeo(privateMeta(lang, 'cartTitle'));
  const navigate = useNavigate();

  const [cart, setCart] = useState<PricedCart | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (lines.length === 0) {
      setCart(null);
      return;
    }

    let cancelled = false;
    setFailed(false);

    api
      .validateCart(lang, { items: lines, currency, deliveryMethod: 'YEREVAN_COURIER' })
      .then((c) => {
        if (!cancelled) setCart(c);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [lang, currency, lines]);

  if (lines.length === 0) {
    return (
      <div className="wrap page empty">
        <p>{t.emptyCart}</p>
        <Link to="/" className="btn">
          {t.goShopping}
        </Link>
      </div>
    );
  }

  return (
    <div className="wrap page">
      <h1>{t.cart}</h1>

      {failed && <div className="notice notice--warn">{t.error}</div>}

      <div className="panel">
        {(cart?.items ?? []).map((item) => {
          const line = lines.find((l) => l.variantId === item.variantId);
          return (
            <div key={item.variantId} className="line">
              <div className="line__body">
                <div className="line__title">{item.title}</div>
                <div className="muted" style={{ fontSize: '0.875rem' }}>
                  {item.variantName} · {money(item.unitPrice, currency, lang)}
                </div>
                {item.issue === 'OUT_OF_STOCK' && (
                  <div className="hand">{t.outOfStock}</div>
                )}
                {item.issue === 'QTY_REDUCED' && (
                  <div className="hand">
                    {t.onlyLeft(item.available)}
                  </div>
                )}
              </div>

              <div className="qty">
                <button
                  type="button"
                  aria-label="−"
                  onClick={() => setQty(item.variantId, (line?.qty ?? 1) - 1)}
                >
                  −
                </button>
                <span>{line?.qty ?? 0}</span>
                <button
                  type="button"
                  aria-label="+"
                  disabled={(line?.qty ?? 0) >= item.available}
                  onClick={() => setQty(item.variantId, (line?.qty ?? 0) + 1)}
                >
                  +
                </button>
              </div>

              <div style={{ minWidth: '5.5rem', textAlign: 'right', fontWeight: 700 }}>
                {money(item.lineTotal, currency, lang)}
              </div>

              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => remove(item.variantId)}
              >
                {t.remove}
              </button>
            </div>
          );
        })}

        {cart && (
          <div className="totals">
            <div>
              <span className="muted">{t.subtotal}</span>
              <span>{money(cart.subtotal, currency, lang)}</span>
            </div>
            {cart.discount > 0 && (
              <div>
                <span className="muted">{t.discount}</span>
                <span>−{money(cart.discount, currency, lang)}</span>
              </div>
            )}
            <div className="grand">
              <span>{t.total}</span>
              <span>{money(cart.subtotal - cart.discount, currency, lang)}</span>
            </div>
          </div>
        )}
      </div>

      {cart && cart.minOrder.required !== null && (
        <div
          className={`notice${cart.minOrder.met ? '' : ' notice--warn'}`}
          style={{ marginTop: '1.5rem' }}
        >
          {cart.minOrder.met
            ? (currency === 'AMD' ? t.minOrderArmenia : t.minOrderWorld)(
                money(cart.minOrder.required, currency, lang),
              )
            : t.minOrderLeft(
                money(
                  cart.minOrder.required - (cart.subtotal - cart.discount),
                  currency,
                  lang,
                ),
              )}
        </div>
      )}

      <button
        type="button"
        className="btn btn--block"
        style={{ marginTop: '1.5rem' }}
        disabled={!cart || !cart.minOrder.met || cart.items.every((i) => i.qty === 0)}
        onClick={() => navigate('/checkout')}
      >
        {t.checkout}
      </button>
    </div>
  );
}
